import { useMutation, useSuspenseQuery } from "@tanstack/react-query";
import { createColumnHelper, Table } from "@tanstack/react-table";
import {
  getControlName,
  pluralize,
  queries,
  service,
  useAuthConfig,
  useDebounceState,
} from "@theprelude/core";
import { Button, notifyError, notifySuccess } from "@theprelude/ds";
import { Control, PartnerGroup } from "@theprelude/sdk";
import { useEffect, useMemo } from "react";
import { isDeepEqual } from "remeda";
import { usePrevious } from "@/hooks/use-previous";
import { fuzzyFilter } from "@/lib/fuzzy-filters";
import { ensureJobCompletion } from "@/queries/jobs";
import { ResultCount } from "../control-monitor/endpoint-tables/result-count";
import { DefaultTooltip } from "../default-tooltip";
import { ResponsiveContainer } from "../responsive-container";
import {
  createCheckboxColumn,
  groupDescriptionColumn,
  groupNameColumn,
  groupSyncColumn,
  groupTypeColumn,
  membershipRuleColumn,
} from "../table/columns";
import { SCMTableFilterInput } from "../table/filter-input";
import { useTable } from "../table/hooks";
import { SelectionBar } from "../table/selection-bar";
import { VirtualTable } from "../table/virtual-table";

const MAX_GROUPS_PER_SYNC = 30;

const columnHelper = createColumnHelper<GroupTableEntry>();

const columns = [
  columnHelper.display(createCheckboxColumn({ showHeader: false })),
  columnHelper.accessor("display_name", groupNameColumn),
  columnHelper.accessor("type", groupTypeColumn),
  columnHelper.accessor("description", groupDescriptionColumn),
  columnHelper.accessor("membership_rule", membershipRuleColumn),
  columnHelper.accessor("isSynced", groupSyncColumn),
];

const getRowId = (group: GroupTableEntry) => group.id;

interface GroupTableEntry extends PartnerGroup {
  isSynced: boolean;
}

export const PartnerGroupTable = ({ instance }: { instance: Control }) => {
  const searchFilter = useDebounceState("");
  const searchValue = searchFilter.debouncedValue.toLowerCase();
  const authConfig = useAuthConfig();

  const partnerGroupsQuery = useSuspenseQuery(
    queries.partner.group(authConfig, instance.id, instance.instance_id),
  );

  const groupsQuery = useSuspenseQuery(queries.scm.groups(authConfig));

  const formattedGroups = useMemo(() => {
    const groups = groupsQuery.data;
    return partnerGroupsQuery.data.map((group) => {
      const sync = groups?.find((g) => g.group_id === group.id);
      return { ...group, isSynced: !!sync };
    });
  }, [groupsQuery.data, partnerGroupsQuery.data]);

  const filteredData = useMemo(() => {
    if (!searchValue) return formattedGroups;

    return formattedGroups.filter((group) => {
      const searchableFields = [
        group.description,
        group.type,
        group.display_name,
        group.membership_rule,
      ].filter(Boolean);

      return searchableFields.some((field) =>
        fuzzyFilter(field?.toLowerCase() ?? "", searchValue),
      );
    });
  }, [searchValue, formattedGroups]);

  const rowSelection = Object.fromEntries(
    formattedGroups
      .filter((group) => group.isSynced)
      .map((group) => [group.id, group.isSynced]),
  );
  const prevRowSelection = usePrevious(rowSelection);

  const table = useTable({
    name: `groups-${instance.instance_id}`,
    data: filteredData,
    columns,
    initialState: {
      sorting: [{ id: groupSyncColumn.id, desc: true }],
      rowSelection,
    },
    state: {
      globalFilter: searchValue,
    },
    getRowId,
  });

  useEffect(() => {
    if (!isDeepEqual(rowSelection, prevRowSelection)) {
      table.setRowSelection(rowSelection);
    }
  }, [rowSelection, prevRowSelection, table]);

  const isLoading = partnerGroupsQuery.isLoading && groupsQuery.isLoading;

  return (
    <div className="flex h-full flex-col gap-4">
      <SCMTableFilterInput
        value={searchFilter.value}
        onChange={(value) => searchFilter.setDelayed(value)}
        onClear={() => searchFilter.setImmediate("")}
      />
      <div className="flex items-center justify-between">
        <ResultCount
          loading={isLoading}
          count={filteredData.length}
          totalCount={partnerGroupsQuery.data?.length ?? 0}
          resourceType="group"
        />
      </div>
      <GroupTable table={table} isLoading={isLoading} />
      <ActionBar table={table} instance={instance} />
    </div>
  );
};

function GroupTable({
  table,
  isLoading,
}: {
  table: Table<GroupTableEntry>;
  isLoading: boolean;
}) {
  return (
    <ResponsiveContainer className="flex-1 overflow-hidden">
      {({ height }) => (
        <VirtualTable table={table} height={height} loading={isLoading} />
      )}
    </ResponsiveContainer>
  );
}

function ActionBar({
  table,
  instance,
}: {
  table: Table<GroupTableEntry>;
  instance: Control;
}) {
  const authConfig = useAuthConfig();
  const selectedRows = table.getSelectedRowModel().flatRows;
  const groupIds = selectedRows.map((row) => row.original.id);
  const { id, instance_id } = instance;
  const updateGroups = useMutation({
    mutationFn: async () => {
      const { job_id: jobId } = await service(
        authConfig,
      ).scm.updatePartnerGroups({
        groupIds,
        instanceId: instance_id,
        control: id,
      });

      return ensureJobCompletion({ authConfig, jobId });
    },
    onSuccess: () => {
      notifySuccess({
        message: `Successfully synced ${getControlName(id)} groups`,
      });
      table.resetRowSelection();
    },
    onError: async (error) => {
      notifyError({
        message: `Error occurred while trying to sync ${getControlName(id)} groups`,
        error,
        dismissible: true,
      });
    },
  });

  if (selectedRows.length === 0) {
    return null;
  }

  return (
    <SelectionBar>
      <Button onClick={() => table.resetRowSelection()} intent="secondary">
        Cancel
      </Button>
      <DefaultTooltip
        content={`Max of ${MAX_GROUPS_PER_SYNC} groups eligible to be synced`}
        disabled={selectedRows.length <= MAX_GROUPS_PER_SYNC}
      >
        <Button
          onClick={() => updateGroups.mutate()}
          intent="primary"
          disabled={
            selectedRows.length > MAX_GROUPS_PER_SYNC || updateGroups.isPending
          }
          loading={updateGroups.isPending}
        >
          Sync {selectedRows.length} {pluralize(selectedRows.length, "group")}
        </Button>
      </DefaultTooltip>
    </SelectionBar>
  );
}
